(() => {
  const element = document.getElementById('PillowcaseFreeGiftConfig');
  if (!element || window.SilkyPillowcaseGift) return;

  const config = JSON.parse(element.textContent);
  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const giftProperty = '_silky_pillowcase_gift';
  const giftValue = 'matching-small-scrunchie';
  const mapping = new Map(config.variants.map((variant) => [String(variant.pillowcaseVariantId), variant.giftVariantId]));
  let queue = Promise.resolve();
  const approvedForms = new WeakSet();
  let pendingCheckoutForms = new WeakSet();
  const knownLines = new Map();
  const cartAdds = new Map();

  const enqueue = (operation) => {
    const result = queue.catch(() => {}).then(operation);
    queue = result.catch(() => {});
    return result;
  };

  const isGift = (item) => String(item.product_id) === String(config.giftProductId);
  const quantity = (value) => Math.max(0, Math.floor(Number(value) || 0));
  const rememberLines = (cart) => {
    for (const item of cart.items || []) knownLines.set(item.key, item);
    while (knownLines.size > 500) knownLines.delete(knownLines.keys().next().value);
    return cart;
  };
  const readCart = async () => {
    const response = await nativeFetch(`${root}cart.js`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Cart unavailable');
    return rememberLines(await response.json());
  };

  const propertiesKey = (properties) => JSON.stringify(Object.entries(Array.isArray(properties) ? Object.fromEntries(properties) : (properties || {})).sort(([left], [right]) => left.localeCompare(right)));
  const describeChange = (init) => {
    if (typeof init?.body !== 'string') return null;
    let body;
    try { body = JSON.parse(init.body); } catch { return null; }
    if (typeof body.id !== 'string' || !body.id.includes(':')) return null;
    const known = knownLines.get(body.id);
    const field = Array.from(document.querySelectorAll('[data-index]')).find((node) => node.getAttribute('data-index') === body.id);
    const row = field?.closest('[data-variant-id]');
    let properties = known?.properties;
    if (row?.hasAttribute('data-item-properties')) {
      try { properties = JSON.parse(row.getAttribute('data-item-properties')); } catch {}
    }
    return {
      body,
      variantId: row?.getAttribute('data-variant-id') || known?.variant_id || body.id.split(':')[0],
      quantity: field?.defaultValue || known?.quantity,
      properties: properties === undefined ? null : propertiesKey(properties)
    };
  };

  const currentChange = async (change, init) => {
    if (!change) return init;
    const cart = await readCart();
    if (cart.items.some((item) => item.key === change.body.id)) return init;
    const candidates = cart.items.filter((item) => String(item.variant_id) === String(change.variantId) && (change.properties === null || propertiesKey(item.properties) === change.properties));
    const matching = candidates.find((item) => quantity(item.quantity) === quantity(change.quantity)) || (candidates.length === 1 ? candidates[0] : null);
    if (!matching) return init;
    return { ...init, body: JSON.stringify({ ...change.body, id: matching.key }) };
  };

  const plan = (cart) => {
    const desired = new Map();
    for (const item of cart.items) {
      if (String(item.product_id) !== String(config.pillowcaseProductId)) continue;
      const giftId = mapping.get(String(item.variant_id));
      if (!giftId) throw new Error('Matching gift variant unavailable');
      const id = String(giftId);
      desired.set(id, (desired.get(id) || 0) + quantity(item.quantity));
    }
    const updates = {};
    for (const item of cart.items.filter(isGift)) {
      const id = String(item.variant_id);
      const needed = desired.get(id) || 0;
      const keep = Math.min(quantity(item.quantity), needed);
      if (keep !== quantity(item.quantity)) updates[item.key] = keep;
      desired.set(id, Math.max(0, needed - keep));
    }
    const additions = Array.from(desired, ([id, count]) => ({
      id: Number(id), quantity: count, properties: { [giftProperty]: giftValue }
    })).filter((item) => item.quantity > 0);
    return { updates, additions };
  };

  const showError = () => {
    let notice = document.getElementById('PillowcaseFreeGiftError');
    if (!notice) {
      notice = document.createElement('p');
      notice.id = 'PillowcaseFreeGiftError';
      notice.className = 'alert alert--error page-width';
      notice.setAttribute('role', 'alert');
      (document.getElementById('MainContent') || document.body).prepend(notice);
    }
    notice.textContent = config.errorMessage;
  };

  const writeCart = async (action, body) => {
    const response = await nativeFetch(`${root}cart/${action}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('Gift cart update failed');
    return response.json();
  };

  const reconcile = async () => {
    let cart = await readCart();
    const changes = plan(cart);
    let changed = false;
    if (Object.keys(changes.updates).length) {
      cart = await writeCart('update', { updates: changes.updates });
      changed = true;
    }
    if (changes.additions.length) {
      await writeCart('add', { items: changes.additions });
      cart = await readCart();
      changed = true;
    }
    const paidGifts = cart.items.filter((item) => isGift(item) && Number(item.final_line_price) > 0);
    if (paidGifts.length) {
      await writeCart('update', { updates: Object.fromEntries(paidGifts.map((item) => [item.key, 0])) });
      throw new Error('Gift discount did not apply');
    }
    const remaining = plan(cart);
    if (remaining.additions.length || Object.keys(remaining.updates).length) throw new Error('Gift cart update incomplete');
    document.getElementById('PillowcaseFreeGiftError')?.remove();
    return { cart, changed };
  };

  const sectionIds = () => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
    document.querySelectorAll('cart-items[data-section-id]').forEach((item) => sections.push(item.getAttribute('data-section-id')));
    return Array.from(new Set(sections)).filter(Boolean).slice(0, 5);
  };

  const renderSections = async (ids) => {
    if (!ids.length) return {};
    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    url.searchParams.delete('section_id');
    const response = await nativeFetch(url.href, { cache: 'no-store' });
    if (!response.ok) throw new Error('Cart sections unavailable');
    return response.json();
  };

  const refresh = async ({ cart, changed }) => {
    if (!changed) return cart;
    cart.sections = await renderSections(sectionIds());
    window.theme?.pubsub?.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'pillowcase-free-gift', cart });
    return cart;
  };

  const addBeforeCart = ({ owner, body, buttons = [], onError }) => {
    const previous = cartAdds.get(owner);
    if (previous?.promise) return previous.promise;
    if (previous?.complete) return Promise.resolve(true);
    const isFormData = body instanceof FormData;
    const fingerprint = JSON.stringify(isFormData ? Array.from(body, ([key, value]) => [key, value instanceof File ? [value.name, value.size, value.lastModified] : value]) : body);
    const state = previous?.fingerprint === fingerprint ? previous : { fingerprint, added: false };
    const submitButtons = Array.from(new Set(buttons)).filter(Boolean);
    state.restore = () => {
      for (const button of submitButtons) {
        button.removeAttribute('aria-busy');
      }
      state.restore = null;
    };
    for (const button of submitButtons) {
      button.setAttribute('aria-busy', 'true');
    }
    cartAdds.set(owner, state);
    state.promise = enqueue(async () => {
      if (!state.added) {
        const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
        if (!isFormData) headers['Content-Type'] = 'application/json';
        const response = await nativeFetch(`${root}cart/add.js`, {
          method: 'POST', headers, body: isFormData ? body : JSON.stringify(body)
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          const error = new Error('Product cart addition failed');
          error.cartMessage = result.description || result.message;
          throw error;
        }
        state.added = true;
      }
      await reconcile();
      state.complete = true;
      window.location.assign(window.theme?.routes?.cart_url || `${root}cart`);
      return true;
    }).catch((error) => {
      if (onError) onError(error.cartMessage || config.errorMessage);
      else showError();
      console.error('Pillowcase gift:', error);
      state.restore?.();
      return false;
    }).finally(() => {
      state.promise = null;
    });
    return state.promise;
  };

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    for (const state of cartAdds.values()) state.restore?.();
    cartAdds.clear();
  });

  window.fetch = function(input, init) {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.href);
    const method = (init?.method || input?.method || 'GET').toUpperCase();
    const match = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/);
    if (!match || method !== 'POST' || url.origin !== window.location.origin) return nativeFetch(input, init);
    const change = match[1] === 'change' ? describeChange(init) : null;
    return enqueue(async () => {
      const currentInit = await currentChange(change, init);
      const response = await nativeFetch(input, { ...currentInit, signal: null });
      if (!response.ok && response.status !== 422) return response;
      const payload = await response.clone().json();
      let result;
      try {
        result = await reconcile();
      } catch (error) {
        showError();
        console.error('Pillowcase gift:', error);
        result = { cart: await readCart(), changed: true };
      }
      if (!result.changed) return response;
      const ids = Object.keys(payload.sections || {});
      const sections = ids.length ? await renderSections(ids) : undefined;
      const body = match[1] === 'add' || !response.ok ? payload : result.cart;
      if (sections) body.sections = sections;
      const headers = new Headers(response.headers);
      headers.delete('content-length');
      headers.delete('content-encoding');
      return new Response(JSON.stringify(body), { status: response.status, statusText: response.statusText, headers });
    });
  };

  window.addEventListener('pageshow', () => {
    pendingCheckoutForms = new WeakSet();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || approvedForms.has(form) || event.submitter?.name !== 'checkout') return;
    const url = new URL(form.action, window.location.href);
    if (url.origin !== window.location.origin || !/\/cart\/?$/.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pendingCheckoutForms.has(form)) return;
    pendingCheckoutForms.add(form);
    const submitter = event.submitter;
    let currentForm = form;
    let currentSubmitter = submitter;
    const resetCheckout = () => {
      pendingCheckoutForms.delete(form);
      pendingCheckoutForms.delete(currentForm);
      submitter?.removeAttribute('aria-busy');
      currentSubmitter?.removeAttribute('aria-busy');
    };
    submitter?.setAttribute('aria-busy', 'true');
    enqueue(() => reconcile()).then(async (result) => {
      await refresh(result);
      currentForm = (form.id && document.getElementById(form.id)) || form;
      currentSubmitter = (submitter?.id && document.getElementById(submitter.id)) || (currentForm === form ? submitter : currentForm.querySelector('[name="checkout"]'));
      if (!currentForm.reportValidity()) {
        resetCheckout();
        return;
      }
      pendingCheckoutForms.add(currentForm);
      currentSubmitter?.setAttribute('aria-busy', 'true');
      approvedForms.add(currentForm);
      try {
        if (typeof currentForm.requestSubmit === 'function') {
          currentForm.requestSubmit(currentSubmitter);
        } else {
          const button = currentSubmitter || currentForm.appendChild(Object.assign(document.createElement('button'), { type: 'submit', name: 'checkout', hidden: true }));
          button.click();
          if (!currentSubmitter) button.remove();
        }
      } finally {
        approvedForms.delete(currentForm);
      }
    }).catch((error) => {
      resetCheckout();
      showError();
      console.error('Pillowcase gift:', error);
    });
  }, true);

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    enqueue(() => reconcile()).then(() => window.location.assign(url.href)).catch((error) => {
      showError();
      console.error('Pillowcase gift:', error);
    });
  }, true);

  window.SilkyPillowcaseGift = {
    plan,
    containsPillowcase: (ids) => ids.some((id) => mapping.has(String(id))),
    addBeforeCart,
    sync: () => enqueue(() => reconcile()).then(refresh),
    ready: queue
  };
  const ready = () => {
    window.SilkyPillowcaseGift.ready = window.SilkyPillowcaseGift.sync().catch((error) => {
      showError();
      console.error('Pillowcase gift:', error);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();
})();
